'use client';

import {
  useCallback,
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { useCopy } from '@/_hooks/useCopy';
import type { DocumentItem, DirectoryType } from '@/_types/models/documents';
import type {
  DocumentsProviderProps,
  DocumentViewType,
} from './DocumentsProvider.types';
import { UserContext } from '../UserProvider';
import { downloadDocument, getDocuments } from '@/_api/client-documents';
import { DocumentsUploadDialog } from './components/DocumentsUploadDialog';
import { getCookie, setCookie } from 'cookies-next/client';
import { FolderCreateDialog } from './components/FolderCreateDialog';
import { getFileItemPath } from '@/_utils/documents';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { sanitizeS3Name } from '@/_utils/sanitize';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ToastContext } from '@/_contexts/ToastProvider';
import { createUrlWithParams } from '@/_utils/params';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { FirmSettingsContext } from '../FirmSettingsProvider';
import { DEFAULT_APP_URL, DEV_APP_URL } from '@/_constants/urls';
import { PreviewContext } from '@/_contexts/PreviewProvider';

const IS_DEVELOPMENT: boolean = process.env.NODE_ENV === 'development';

const DocumentsContext = createContext<DocumentsProviderProps>({
  addNewFolder: (_name, _directory) => null,
  companyId: undefined,
  directories: [],
  rootDirectories: [],
  handleIncomingPath: () => null,
  isLoading: true,
  loadRootDirectories: async () => Promise.resolve(),
  onShowAllFiles: () => null,
  onToggleDirectory: (_item: DirectoryType, _path: string) => null,
  onCopyFileLocation: (_item: DocumentItem) => null,
  onCopyFolderLocation: (_folder: string) => null,
  onToggleFolderCreateDialog: () => null,
  openDirectory: '',
  reloadDocuments: (_path?: string, _showWithDeleted?: boolean) => null,
  reloadFiles: (_path?: string) => null,
  searchValue: '',
  selectedDocuments: [],
  setOpenDirectory: () => null,
  setSearchValue: () => null,
  setSelectedDocuments: () => null,
  setShowWithDeleted: () => null,
  setUploading: () => null,
  setView: () => null,
  showWithDeleted: false,
  view: 'list',
});

const COOKIE_DOCUMENT_VIEW = 'SORABAN_COOKIE_DOCUMENT_VIEW' as const;

// Utility function to extract base path from directories
const getBasePath = (directories: Array<DirectoryType>): string => {
  return directories?.[0]?.link?.split('/').slice(0, -1)?.join('/') || '';
};

const DocumentsProvider: any = ({ children }: any) => {
  const { token, selectedCompanyId, isAdvisor } = useContext(UserContext);
  const { previewUserCompanyId } = useContext(UserPreviewContext);
  const { selectedCompany, selectedHash } = useContext(CompanyContext);
  const { addToast } = useContext(ToastContext);
  const { firm } = useContext(FirmSettingsContext);

  const savedCookie: string | undefined = getCookie(COOKIE_DOCUMENT_VIEW);
  const savedViewState: DocumentViewType = useMemo(() => {
    if (savedCookie === 'list' || savedCookie === 'grid') return savedCookie;
    return 'list';
  }, [savedCookie]);

  const companyId: number | null | undefined = useMemo(
    () =>
      isAdvisor
        ? previewUserCompanyId || selectedCompany?.id
        : selectedCompanyId,
    [selectedCompanyId, selectedCompany, previewUserCompanyId, isAdvisor],
  );

  const {
    setPreviewFile,
    setPreviewFileOriginalLink,
    setPreviewCompanyIdForAnnotations,
  } = useContext(PreviewContext);
  const { copyTextToClipboard } = useCopy();

  const searchParams = useSearchParams();

  const [searchValue, setSearchValue] = useState<string>('');

  const fileParam: string | null =
    searchParams?.get('file') || searchParams?.get('previewName');
  const pathParam: string | null = searchParams?.get('path');
  const pathname = usePathname();
  const { replace } = useRouter();
  const isDocumentPage: boolean = pathname.includes('documents');

  const [isCreatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [view, setView] = useState<DocumentViewType>(savedViewState);
  const [isUploading, setUploading] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [openDirectory, setOpenDirectory] = useState<string>('');
  const [directories, setDirectories] = useState<Array<DirectoryType>>([]);
  const [rootDirectories, setRootDirectories] = useState<Array<DirectoryType>>(
    [],
  );
  const [selectedDocuments, setSelectedDocuments] = useState<
    Array<DocumentItem>
  >([]);
  const [showWithDeleted, setShowWithDeleted] = useState<boolean>(false);

  const onUpdateView = (view: DocumentViewType) => {
    setCookie(COOKIE_DOCUMENT_VIEW, view || 'list');

    return setView(view || 'list');
  };

  const _constructUrl = (path: string, file?: string): string => {
    try {
      const baseUrl: string = IS_DEVELOPMENT
        ? DEV_APP_URL
        : firm?.subdomain_url || DEFAULT_APP_URL;
      const newUrl = new globalThis.URL('/documents', baseUrl);

      newUrl.pathname = `/documents/${selectedHash}`;
      newUrl.searchParams.set('path', encodeURIComponent(path));

      if (file) {
        newUrl.searchParams.set('file', encodeURIComponent(file));
      }

      return newUrl.toString();
    } catch (error) {
      console.error(error);
      return '';
    }
  };

  const onCopyFileLocation = (item: DocumentItem) => {
    const newFileUrl = _constructUrl(openDirectory, item.name);
    copyTextToClipboard(newFileUrl);
  };

  const onCopyFolderLocation = () => {
    const newFolderUrl = _constructUrl(openDirectory);
    copyTextToClipboard(newFolderUrl);
  };

  const _updateTreeRecursive = (
    data: Array<DocumentItem> = [],
    itemLink: string,
    childrenDocuments: Array<DocumentItem> = [],
  ): Array<DirectoryType> => {
    const childrenFolders: Array<DirectoryType> = childrenDocuments.filter(
      (cd) => cd.type === 'folder',
    );

    return data
      .filter((document) => document.type === 'folder')
      .map((document) => {
        if (
          document.link === itemLink ||
          (itemLink && document.link.endsWith(itemLink))
        ) {
          return { ...document, children: childrenFolders };
        } else if (document.children) {
          return {
            ...document,
            children: _updateTreeRecursive(
              document.children,
              itemLink,
              childrenFolders,
            ),
          };
        }

        return document;
      });
  };

  // Toggle open folders
  const onToggleDirectory = async (item: DirectoryType) => {
    setSearchValue('');
    if (!companyId || !token) return;

    const realPath = getFileItemPath(item?.link);

    if (isDocumentPage) {
      const newUrl = createUrlWithParams(
        pathname,
        searchParams,
        'path',
        realPath,
      );

      replace(newUrl, { scroll: false });
    }

    const { documents } = (await getDocuments({
      companyId,
      path: realPath,
      showWithDeleted,
      token,
    })) as { documents: Array<DocumentItem> };

    const updatedData = _updateTreeRecursive(directories, item.link, documents);
    const updatedRootData = _updateTreeRecursive(
      rootDirectories,
      item.link,
      documents,
    );

    setDirectories(updatedData);
    setRootDirectories(updatedRootData);
    setSelectedDocuments(documents);
    setOpenDirectory(realPath);
  };

  const addNewFolder = (name: string, directory: string) => {
    const sanitizedName: string = sanitizeS3Name(name);

    if (!sanitizedName) return;

    let newFolder: DirectoryType | undefined;

    const folderPieces = directory.split('/').filter(Boolean);

    // Special case: Adding folder to root directory
    if (!folderPieces?.length) {
      // Get the base path from root directories
      const rootBasePath = getBasePath(rootDirectories);

      const newRootFolder: DirectoryType = {
        annotations: [],
        children: [],
        created_at: new Date().toISOString(),
        description: '',
        extracted_document_id: null,
        items_count: 0,
        link: `${rootBasePath}/${sanitizedName}`.replace(/\/+/g, '/'),
        name: sanitizedName,
        questionnaire_id: null,
        tags: null,
        type: 'folder',
        updated_at: null,
      };

      // Add to root directories
      const updatedRootDirectories = [...rootDirectories, newRootFolder];
      setRootDirectories(updatedRootDirectories);

      // Add to current directories if we're viewing the root
      if (openDirectory === '' || openDirectory === '/') {
        const updatedDirectories = [...directories, newRootFolder];
        setDirectories(updatedDirectories);
        const newDocuments: Array<DocumentItem> = [
          newRootFolder,
          ...selectedDocuments,
        ];
        setSelectedDocuments(newDocuments);
      } else {
        // If we're not viewing root, just reload the current documents
        reloadDocuments();
      }

      return;
    }

    const newDirectories = [...directories];
    const baseFolderName = getBasePath(newDirectories);

    const addFolderRecursively = (
      dirs: DirectoryType[],
      pieces: string[],
      currentPath: string = '',
      isForRootDirectories: boolean = false,
    ): DirectoryType[] => {
      if (pieces.length === 0) {
        // For root directories, use the root structure base path
        const rootBaseFolderName =
          isForRootDirectories && rootDirectories?.length > 0
            ? getBasePath(rootDirectories)
            : baseFolderName;

        const newFolderName: string = `${rootBaseFolderName}${currentPath}/${sanitizedName}`;

        newFolder = {
          annotations: [],
          children: [],
          created_at: new Date().toISOString(),
          description: '',
          extracted_document_id: null,
          items_count: 0,
          link: newFolderName,
          name: sanitizedName,
          questionnaire_id: null,
          tags: null,
          type: 'folder',
          updated_at: null,
        };

        if (!dirs.find((dir) => dir.name === name)) {
          dirs.push(newFolder);
        }
        return dirs;
      }

      const currentPiece: string = pieces[0];
      const updatedPath: string = `${currentPath}/${currentPiece}`.replace(
        /\/+/g,
        '/',
      );

      let existingFolder = dirs.find((dir) => dir.name === currentPiece);

      if (!existingFolder) {
        const rootBaseFolderName =
          isForRootDirectories && rootDirectories?.length > 0
            ? getBasePath(rootDirectories)
            : baseFolderName;

        existingFolder = {
          annotations: [],
          children: [],
          created_at: new Date().toISOString(),
          description: '',
          extracted_document_id: null,
          items_count: 0,
          link: `${rootBaseFolderName}/${updatedPath}`.replace(/\/+/g, '/'),
          name: currentPiece,
          questionnaire_id: null,
          tags: null,
          type: 'folder',
          updated_at: null,
        };

        dirs.push(existingFolder);
      }

      existingFolder.children = addFolderRecursively(
        existingFolder.children as Array<DirectoryType>,
        pieces.slice(1),
        updatedPath,
        isForRootDirectories,
      );

      return dirs;
    };

    const updatedDirectories = addFolderRecursively(
      newDirectories,
      folderPieces,
      '',
      false,
    );

    setDirectories(updatedDirectories);

    // For root directories, handle them separately with the correct context
    const newRootDirectories = [...rootDirectories];
    const updatedRootDirectories = addFolderRecursively(
      newRootDirectories,
      folderPieces,
      '',
      true,
    );
    setRootDirectories(updatedRootDirectories);

    const newDocuments: Array<DocumentItem> = newFolder
      ? [newFolder, ...selectedDocuments]
      : selectedDocuments;

    return setSelectedDocuments(newDocuments);
  };

  const reloadDocuments = useCallback(
    async (
      path: string | undefined = '',
      showWithDeletedOverride: boolean | undefined = undefined,
    ) => {
      if (!companyId || !token) return;

      console.debug('--> reloadDocuments', { companyId, path });

      try {
        setLoading(true);
        const { documents } = (await getDocuments({
          companyId,
          path: path ?? '',
          showWithDeleted:
            typeof showWithDeletedOverride === 'boolean'
              ? showWithDeletedOverride
              : showWithDeleted,
          token,
        })) as { documents: Array<DirectoryType> };

        setDirectories(documents);
        setSelectedDocuments(documents);

        // Load root directories if not already loaded or if it's empty
        if (!rootDirectories.length && (path ?? '') === '') {
          setRootDirectories(documents);
        }
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoading(false);
        setOpenDirectory(path ?? '');

        if (isDocumentPage) {
          const newSearchParams = createUrlWithParams(
            pathname,
            searchParams,
            'path',
            path ?? '',
          );

          replace(newSearchParams);
        }
      }
    },
    [
      addToast,
      companyId,
      isDocumentPage,
      pathname,
      replace,
      searchParams,
      showWithDeleted,
      token,
      rootDirectories.length,
    ],
  );

  const loadRootDirectories = useCallback(
    async (forceReload: boolean = false) => {
      if (!companyId || !token) return;

      // Don't reload if we already have root directories unless forced
      if (!forceReload && rootDirectories?.length > 0) {
        return;
      }

      try {
        const { documents } = (await getDocuments({
          companyId,
          path: '',
          showWithDeleted,
          token,
        })) as { documents: Array<DirectoryType> };

        setRootDirectories(documents);
      } catch (error) {
        console.error('Error loading root directories:', error);
      }
    },
    [companyId, token, showWithDeleted, rootDirectories?.length],
  );

  const onShowAllFiles = useCallback(() => {
    setOpenDirectory('');
    reloadDocuments('');
  }, [reloadDocuments]);

  const onCloseUploadDialog = () => {
    setUploading(false);
    reloadFiles();
  };

  const reloadFiles = async (path: string | undefined = undefined) => {
    if (!token) return;

    const { documents } = (await getDocuments({
      companyId: companyId!,
      path: typeof path === 'string' ? path : openDirectory,
      showWithDeleted,
      token,
    })) as { documents: Array<DocumentItem> };

    const updatedData = _updateTreeRecursive(
      directories,
      typeof path === 'string' ? path : openDirectory,
      documents,
    );

    const updatedRootData = _updateTreeRecursive(
      rootDirectories,
      typeof path === 'string' ? path : openDirectory,
      documents,
    );

    setDirectories(updatedData);
    setRootDirectories(updatedRootData);
    setSelectedDocuments(documents);
  };

  const onToggleFolderCreateDialog = () => setCreatingFolder(!isCreatingFolder);

  const _gotoPath = (directory: string = '') => {
    const newUrl: string = createUrlWithParams(
      pathname,
      searchParams,
      'path',
      directory,
    );

    return replace(newUrl);
  };

  const _findAndUpdatePath = async (
    tree: Array<DirectoryType>,
    targetPath: string,
    token: string,
    companyId: number,
  ): Promise<Array<DirectoryType>> => {
    const updatedTree = await Promise.all(
      tree.map(async (item) => {
        if (item.type !== 'folder') return item;

        const itemPath = getFileItemPath(item.link);

        if (targetPath.startsWith(itemPath)) {
          const { documents: folderContents } = await getDocuments({
            companyId,
            path: itemPath,
            showWithDeleted,
            token,
          });

          const updatedChildren = await _findAndUpdatePath(
            folderContents.filter((doc) => doc.type === 'folder'),
            targetPath,
            token,
            companyId,
          );

          return {
            ...item,
            children: updatedChildren,
          };
        }

        return item;
      }),
    );

    return updatedTree;
  };

  const _handleIncomingPath = async (path: string | null = '') => {
    if (!token || !companyId) return;

    try {
      const decodedPath = path && path !== '/' ? decodeURIComponent(path) : '';
      const { documents: rootStructure } = await getDocuments({
        companyId,
        path: '',
        showWithDeleted,
        token,
      });

      const updatedDirectories = decodedPath
        ? await _findAndUpdatePath(
            rootStructure as Array<DirectoryType>,
            decodedPath,
            token,
            companyId,
          )
        : (rootStructure as Array<DirectoryType>);

      const { documents: targetContents } = await getDocuments({
        companyId,
        path: decodedPath,
        showWithDeleted,
        token,
      });

      setDirectories(updatedDirectories as Array<DirectoryType>);
      setRootDirectories(updatedDirectories as Array<DirectoryType>);
      setSelectedDocuments(targetContents as Array<DocumentItem>);
      setOpenDirectory(decodedPath);

      if (isDocumentPage) {
        _gotoPath(decodedPath);
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const _downloadPreviewFile = useCallback(async () => {
    try {
      const selectedFileItemLink: string | undefined = selectedDocuments?.find(
        (d) => d.name === fileParam,
      )?.link;

      if (selectedFileItemLink) {
        const { document } = await downloadDocument({
          companyId: companyId!,
          link: selectedFileItemLink,
          token,
        });

        // Set metadata before preview file to avoid race conditions
        setPreviewCompanyIdForAnnotations(companyId);
        setPreviewFileOriginalLink(selectedFileItemLink);
        // Set preview file last to trigger dialog with all metadata ready
        setPreviewFile(document?.data?.link);
      }
    } catch (error) {
      console.error(error);
    }
  }, [
    companyId,
    fileParam,
    selectedDocuments,
    token,
    setPreviewFile,
    setPreviewFileOriginalLink,
    setPreviewCompanyIdForAnnotations,
  ]);

  useEffect(() => {
    if (fileParam) {
      _downloadPreviewFile();
    }
  }, [_downloadPreviewFile, fileParam]);

  useEffect(() => {
    const initializeDocuments = async () => {
      if (!token || !companyId) return;

      if (!directories?.length) {
        setLoading(true);
      }

      try {
        // Initialize file tree only if it's empty
        if (!rootDirectories?.length) {
          await loadRootDirectories(false);
        }

        if (pathParam && !directories?.length) {
          await _handleIncomingPath(pathParam);
        } else if (!directories?.length) {
          await reloadDocuments('');
        } else {
          setOpenDirectory(pathParam || '');
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    initializeDocuments();
  }, [token, companyId, pathParam]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DocumentsContext
      value={{
        addNewFolder,
        companyId,
        directories,
        rootDirectories,
        handleIncomingPath: _handleIncomingPath,
        isLoading,
        loadRootDirectories,
        onCopyFileLocation,
        onCopyFolderLocation,
        onShowAllFiles,
        onToggleDirectory,
        onToggleFolderCreateDialog,
        openDirectory,
        reloadDocuments,
        reloadFiles,
        searchValue,
        selectedDocuments,
        setOpenDirectory,
        setSearchValue,
        setSelectedDocuments,
        setShowWithDeleted,
        setUploading,
        setView: onUpdateView,
        showWithDeleted,
        view,
      }}
    >
      <DocumentsUploadDialog
        isOpen={isUploading}
        onClose={onCloseUploadDialog}
        companyId={companyId}
      />
      <FolderCreateDialog
        isOpen={isCreatingFolder}
        onClose={onToggleFolderCreateDialog}
        companyId={companyId}
      />
      {children}
    </DocumentsContext>
  );
};

export { DocumentsContext, DocumentsProvider };
